import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Row, Col, Button, Input, Label, Table, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import Header from "../../../layout/Header/Header";
import { CampoEscaneo, codigosDe, sumarEscaneo } from "../../Escaneo";

// Recepciones: todo lo pendiente por recibir (embarques internacionales y OC nacionales). Se recibe lo
// que realmente llego, a mano o escaneando (solo entra lo escaneado); lo que falte sigue pendiente
// hasta que llegue o se cierre. POST /comprasRecepcion hace la Entrada, el inventario y (nacional) la
// cuenta por pagar por lo recibido.

const fmt = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n || 0);

function ListadoRecepciones() {
  const { user } = useContext(AuthContext);
  const URL_EMBARQUES = process.env.REACT_APP_URL_EMBARQUES;
  const URL_ARTICULOS_EMBARQUES = process.env.REACT_APP_URL_ARTICULOS_EMBARQUES;
  const URL_COMPRAS = process.env.REACT_APP_URL_COMPRAS;
  const URL_AREAS = process.env.REACT_APP_URL_AREAS;
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const AREAS_CEDIS = process.env.REACT_APP_AREAS_CEDIS;
  const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

  const [pendientes, setPendientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [areas, setAreas] = useState([]);
  const [buscar, setBuscar] = useState("");

  // Recepcion abierta
  const [embarque, setEmbarque] = useState(null);
  const [renglones, setRenglones] = useState([]);
  const [modo, setModo] = useState("manual");
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [area, setArea] = useState(AREAS_CEDIS);
  const [tipoCambio, setTipoCambio] = useState(1);
  const [factura, setFactura] = useState("NA");
  const [observaciones, setObservaciones] = useState("NA");
  const [guardando, setGuardando] = useState(false);


  const cargar = () => {
    setCargando(true);
    axios
      .get(`${URL_EMBARQUES}RecepcionPendiente`, { headers })
      .then((res) => setPendientes(res.data || []))
      .catch((err) => console.log(err))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    cargar();
    axios
      .get(URL_AREAS, { headers })
      .then((res) => setAreas((res.data || []).filter((a) => a.is_active !== "No")))
      .catch((err) => console.log(err));
  }, []);

  const abrir = (e) => {
    setEmbarque(e);
    setModo("manual");
    setFecha(moment().format("YYYY-MM-DD"));
    setArea(AREAS_CEDIS);
    setTipoCambio(e.tipoCambioUSDMXN || 1);
    setFactura("NA");
    setObservaciones("NA");
    setRenglones([]);
    axios
      .get(`${URL_ARTICULOS_EMBARQUES}/embarque/${e._id}`, { headers })
      .then((res) => {
        const rs = (res.data || [])
          .filter((a) => a.pendiente_recibir > 0)
          .map((a) => {
            const art = (a.articulos && a.articulos[0]) || {};
            const oc = a.articulosOC && a.articulosOC[0] && a.articulosOC[0].ordenesCompra && a.articulosOC[0].ordenesCompra[0];
            return {
              id: a._id,
              SKU: art.SKU,
              nombre: art.nombre,
              esMaster: art.modelo === "MASTER",
              oc: oc ? oc.idOrdenCompra : "",
              pedido: a.cantidad,
              recibidoPrev: a.recibido,
              tope: a.pendiente_recibir,
              cantidad: a.pendiente_recibir,
              codigos: codigosDe(art),
              etiqueta: `${art.SKU} ${art.nombre || ""}`,
            };
          })
          .sort((x, y) => String(x.SKU).localeCompare(String(y.SKU)));
        setRenglones(rs);
      })
      .catch((err) => console.log(err));
  };

  const cambiarModo = (m) => {
    setModo(m);
    // Manual: arranca con todo lo pendiente; escaneando: en 0 y solo entra lo escaneado
    setRenglones(renglones.map((r) => ({ ...r, cantidad: m === "manual" ? r.tope : 0 })));
  };

  // Handheld: se escanea el articulo una vez y se captura la cantidad (CampoEscaneo)
  const escanear = (codigo, cantidad) => {
    setRenglones(sumarEscaneo(renglones, codigo, cantidad).renglones);
  };

  const capturar = (id, valor) => {
    setRenglones(
      renglones.map((r) => {
        if (r.id !== id) return r;
        const v = valor === "" ? "" : Math.max(0, parseFloat(valor) || 0);
        if (v !== "" && v > r.tope) {
          Swal.fire({ icon: "warning", title: `Solo hay ${r.tope} pendientes de ${r.SKU}`, timer: 1800, showConfirmButton: false });
          return { ...r, cantidad: r.tope };
        }
        return { ...r, cantidad: v };
      })
    );
  };

  const totalPendiente = renglones.reduce((t, r) => t + (parseFloat(r.tope) || 0), 0);
  const totalRecibir = renglones.reduce((t, r) => t + (parseFloat(r.cantidad) || 0), 0);

  const guardar = async () => {
    const aRecibir = renglones.filter((r) => (parseFloat(r.cantidad) || 0) > 0);
    if (!aRecibir.length) {
      Swal.fire({ icon: "error", title: "No hay nada que recibir", text: "Captura o escanea lo que llegó." });
      return;
    }
    if (!area) {
      Swal.fire({ icon: "error", title: "Elige el almacén" });
      return;
    }
    const faltan = totalPendiente - totalRecibir;
    const r = await Swal.fire({
      icon: faltan > 0 ? "warning" : "question",
      title: `Recibir ${fmt(totalRecibir)} pzs`,
      html:
        faltan > 0
          ? `Quedan <b>${fmt(faltan)} pzs</b> pendientes: seguirán en Recepciones hasta que lleguen o cierres el embarque.`
          : "Se recibe completo.",
      showCancelButton: true,
      confirmButtonText: "Sí, recibir",
      cancelButtonText: "Cancelar",
    });
    if (!r.isConfirmed) return;
    setGuardando(true);
    try {
      const { data } = await axios.post(
        `${URL_COMPRAS}Recepcion`,
        {
          embarques: embarque._id,
          areas: area,
          fecha,
          factura,
          observaciones,
          tipoCambio: parseFloat(tipoCambio) || 1,
          user: user.id,
          renglones: aRecibir.map((x) => ({ articulosEmbarques: x.id, cantidad: parseFloat(x.cantidad) })),
        },
        { headers }
      );
      await axios.post(
        URL_LOGS,
        { tipo: "Recepcion", detalle: `Embarque ${embarque.idEmbarques} / Entrada ${data.compra.idCompras} / ${totalRecibir} pzs`, user: user.id },
        { headers }
      );
      const conv = (data.conversiones || [])
        .map((c) => `${c.master} → ${c.cantidad} ${c.variante}${c.creada ? " (variante nueva)" : ""}`)
        .join("<br>");
      Swal.fire({
        icon: "success",
        title: `Entrada #${data.compra.idCompras}`,
        html:
          `Se recibieron ${fmt(totalRecibir)} pzs del embarque #${embarque.idEmbarques}.` +
          (data.nota ? `<br>Cuenta por pagar #${data.nota.idNotasCxP}: $${fmt(data.nota.total_costo)}` : "") +
          (data.completo ? "<br>El embarque quedó recibido completo." : "<br>Lo que falta sigue pendiente.") +
          (conv ? `<p style="text-align:left;margin-top:8px"><b>Masters que entraron a su variante:</b><br>${conv}</p>` : ""),
      });
      setEmbarque(null);
      cargar();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "No se guardó la recepción",
        text: `${error?.response?.data?.message || error.message}`,
      });
    }
    setGuardando(false);
  };

  const cerrar = async (e) => {
    const r = await Swal.fire({
      icon: "warning",
      title: `Cerrar embarque #${e.idEmbarques}`,
      html: `Las <b>${fmt(e.pendiente)} pzs</b> que faltan se darán por no recibidas y el embarque sale de Recepciones. No mueve inventario${
        e.tipo === "Nacional" ? " ni genera cuenta por pagar por lo que no llegó" : ""
      }.`,
      showCancelButton: true,
      confirmButtonText: "Sí, cerrar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!r.isConfirmed) return;
    try {
      await axios.patch(`${URL_EMBARQUES}CerrarRecepcion/${e._id}`, {}, { headers });
      await axios.post(URL_LOGS, { tipo: "Cerrar Recepcion", detalle: `Embarque ${e.idEmbarques} / ${e.pendiente} pzs no recibidas`, user: user.id }, { headers });
      Swal.fire({ icon: "success", title: "Embarque cerrado", timer: 1500, showConfirmButton: false });
      cargar();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo cerrar", text: `${error?.response?.data?.message || error.message}` });
    }
  };

  const lista = pendientes
    .filter((e) => {
      const q = buscar.trim().toLowerCase();
      if (!q) return true;
      return [e.idEmbarques, e.contenedor, e.proveedor, e.tipo, ...(e.ocs || [])].join(" ").toLowerCase().includes(q);
    })
    .sort((a, b) => b.idEmbarques - a.idEmbarques);

  const badge = (tipo) => (
    <span
      style={{
        fontSize: "0.65rem",
        fontWeight: 700,
        padding: "2px 7px",
        borderRadius: 4,
        color: tipo === "Nacional" ? "#065f46" : "#1e3a8a",
        background: tipo === "Nacional" ? "#d1fae5" : "#dbeafe",
      }}
    >
      {tipo === "Nacional" ? "OC NACIONAL" : "EMBARQUE"}
    </span>
  );

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_compras ? (
        <div className="card container col-12">
          <h3 align="center">Recepciones</h3>
          <p align="center" style={{ fontSize: "0.8rem", color: "#64748b" }}>
            Lo pendiente por recibir de embarques y OC nacionales. Recibe lo que realmente llegó; lo que falte sigue aquí.
          </p>
          <Row style={{ marginBottom: 10 }}>
            <Col md={4}>
              <Input placeholder="Buscar embarque, OC, proveedor..." value={buscar} onChange={(e) => setBuscar(e.target.value)} />
            </Col>
            <Col md={8} style={{ textAlign: "right" }}>
              <Button size="sm" color="success" href="/OrdenesCompraNacionalCreate">
                <i className="fas fa-plus"></i> OC Nacional
              </Button>
              <Button size="sm" href="/MenuCompras">
                Regresar
              </Button>
            </Col>
          </Row>
          {cargando ? (
            <div style={{ textAlign: "center", padding: 30 }}>
              <Spinner />
            </div>
          ) : !lista.length ? (
            <p style={{ textAlign: "center", color: "#94a3b8", padding: 30 }}>No hay nada pendiente por recibir.</p>
          ) : (
            <Table size="sm" hover responsive>
              <thead>
                <tr>
                  <th>Embarque</th>
                  <th>Tipo</th>
                  <th className="d-none d-md-table-cell">Contenedor</th>
                  <th>Proveedor</th>
                  <th>OC</th>
                  <th className="d-none d-md-table-cell">Fecha estimada</th>
                  <th className="d-none d-md-table-cell" style={{ textAlign: "right" }}>Pedido</th>
                  <th className="d-none d-md-table-cell" style={{ textAlign: "right" }}>Recibido</th>
                  <th style={{ textAlign: "right" }}>Pendiente</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lista.map((e) => (
                  <tr key={e._id}>
                    <td>#{e.idEmbarques}</td>
                    <td>{badge(e.tipo)}</td>
                    <td className="d-none d-md-table-cell">{e.contenedor}</td>
                    <td>{e.proveedor}</td>
                    <td>{(e.ocs || []).map((n) => `#${n}`).join(", ")}</td>
                    <td className="d-none d-md-table-cell">{e.fechaEstimada}</td>
                    <td className="d-none d-md-table-cell" style={{ textAlign: "right" }}>{fmt(e.cantidad)}</td>
                    <td className="d-none d-md-table-cell" style={{ textAlign: "right" }}>{fmt(e.recibido)}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(e.pendiente)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <Button size="sm" color="primary" onClick={() => abrir(e)}>
                        <i className="fas fa-dolly"></i> Recibir
                      </Button>
                      <Button size="sm" color="danger" onClick={() => cerrar(e)} title="Dar por no recibido lo que falta">
                        <i className="fas fa-lock"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Modal size="xl" isOpen={!!embarque} toggle={() => !guardando && setEmbarque(null)}>
            {embarque && (
              <>
                <ModalHeader toggle={() => !guardando && setEmbarque(null)}>
                  Recibir embarque #{embarque.idEmbarques} {badge(embarque.tipo)}{" "}
                  <small style={{ color: "#64748b" }}>
                    {embarque.proveedor} · OC {(embarque.ocs || []).map((n) => `#${n}`).join(", ")}
                  </small>
                </ModalHeader>
                <ModalBody>
                  <Row>
                    <Col md={2}>
                      <Label>Fecha</Label>
                      <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
                    </Col>
                    <Col md={3}>
                      <Label>Almacén</Label>
                      <Input type="select" value={area} onChange={(e) => setArea(e.target.value)}>
                        <option value="">Selecciona</option>
                        {areas
                          .sort((a, b) => (a.name > b.name ? 1 : -1))
                          .map((a) => (
                            <option key={a._id} value={a._id}>
                              {a.name}
                            </option>
                          ))}
                      </Input>
                    </Col>
                    {embarque.tipo !== "Nacional" && (
                      <Col md={2}>
                        <Label>Tipo de cambio</Label>
                        <Input type="number" min="0" step="any" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} />
                      </Col>
                    )}
                    <Col md={2}>
                      <Label>Factura</Label>
                      <Input value={factura} onChange={(e) => setFactura(e.target.value)} />
                    </Col>
                    <Col md={3}>
                      <Label>Observaciones</Label>
                      <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
                    </Col>
                  </Row>
                  <Row style={{ marginTop: 12, alignItems: "center" }}>
                    <Col md={4}>
                      <Button size="sm" color={modo === "manual" ? "primary" : "secondary"} outline={modo !== "manual"} onClick={() => cambiarModo("manual")}>
                        <i className="fas fa-keyboard"></i> Manual
                      </Button>
                      <Button size="sm" color={modo === "escaneo" ? "primary" : "secondary"} outline={modo !== "escaneo"} onClick={() => cambiarModo("escaneo")}>
                        <i className="fas fa-barcode"></i> Escaneando
                      </Button>
                    </Col>
                    <Col md={5}>
                      {modo === "escaneo" ? (
                        <CampoEscaneo renglones={renglones} onConfirmar={escanear} />
                      ) : (
                        <small style={{ color: "#64748b" }}>Viene lleno con lo pendiente: corrige lo que realmente llegó.</small>
                      )}
                    </Col>
                    <Col md={3} style={{ textAlign: "right" }}>
                      <h5 style={{ margin: 0 }}>
                        {fmt(totalRecibir)} / {fmt(totalPendiente)} pzs
                      </h5>
                    </Col>
                  </Row>
                  {modo === "escaneo" ? (
                    <div style={{ marginTop: 10 }}>
                      {renglones.map((r) => {
                        const c = parseFloat(r.cantidad) || 0;
                        return (
                          <div
                            key={r.id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "6px 8px",
                              borderBottom: "1px solid #e2e8f0",
                              background: c >= r.tope ? "#dcfce7" : c > 0 ? "#fef3c7" : undefined,
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700 }}>{r.SKU}</div>
                              <div style={{ fontSize: "0.72rem", color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {r.nombre}
                              </div>
                            </div>
                            <div style={{ fontSize: "1.15rem", fontWeight: 700, whiteSpace: "nowrap", marginLeft: 8 }}>
                              {fmt(c)} / {fmt(r.tope)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                  <Table size="sm" style={{ marginTop: 10 }}>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Artículo</th>
                        <th>OC</th>
                        <th style={{ textAlign: "right" }}>Pedido</th>
                        <th style={{ textAlign: "right" }}>Ya recibido</th>
                        <th style={{ textAlign: "right" }}>Pendiente</th>
                        <th style={{ width: 120 }}>Recibir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renglones.map((r) => {
                        const c = parseFloat(r.cantidad) || 0;
                        const color = c >= r.tope ? "#f0fdf4" : c > 0 ? "#fffbeb" : undefined;
                        return (
                          <tr key={r.id} style={{ background: color }}>
                            <td>
                              {r.SKU}
                              {r.esMaster && (
                                <small style={{ display: "block", color: "#9d174d" }}>master: entra a su variante</small>
                              )}
                            </td>
                            <td>{r.nombre}</td>
                            <td>{r.oc ? `#${r.oc}` : ""}</td>
                            <td style={{ textAlign: "right" }}>{fmt(r.pedido)}</td>
                            <td style={{ textAlign: "right" }}>{fmt(r.recibidoPrev)}</td>
                            <td style={{ textAlign: "right" }}>{fmt(r.tope)}</td>
                            <td>
                              <Input
                                bsSize="sm"
                                type="number"
                                min="0"
                                step="any"
                                value={r.cantidad}
                                onChange={(e) => capturar(r.id, e.target.value)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button color="success" onClick={guardar} disabled={guardando || !renglones.length}>
                    {guardando ? <Spinner size="sm" /> : <i className="fas fa-check"></i>} Recibir {fmt(totalRecibir)} pzs
                  </Button>
                  <Button color="secondary" onClick={() => setEmbarque(null)} disabled={guardando}>
                    Cancelar
                  </Button>
                </ModalFooter>
              </>
            )}
          </Modal>
        </div>
      ) : undefined}
    </>
  );
}

export default ListadoRecepciones;
